import React from 'react'
import { Col, Row } from 'react-flexbox-grid'
import set from 'lodash/set'
import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useQuery } from 'components/graphql'
import isIP from 'validator/lib/isIP'

import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import { Checkbox, Form, Number as NumberInput, Select, Text } from 'components/forms'
import Divider from 'components/Divider'
import { ImageUploaderGraphql } from 'components/Image'
import globalStyles from 'style/app.module.scss'
import { loadAppConstants } from 'gql/query'
import getCashRegister from '../../../gql/getCashRegister'
import PrinterLocation from './KitchenPrinters'
import MobileWaiterReceiptPrinters from './MobileWaiterReceiptPrinters'
import { ReceiptSettingsTitle } from './styled'

export const sunmiCloudPrintersQuery = gql`
query listDevices($params: ListQueryArgs, $model: String, $software: String) {
  listDevices(filter: {
		model: $model,
		software: $software
  }, params: $params) {
		cursors {
			after
    		hasNext
    }
    items {
    	hardwareId
    }
  }
}
`

const Printers = ({ handleSubmit, formData, data, updateItem, appData }) => {
	const { id, deviceType, parameters = {} } = data
	const [t, i18n] = useTranslation()
	const cashRegisterId = id || formData?.id
	const supportsMultipleReceiptPrinters = deviceType === 'MOBILE_WAITER'


	const devicesRes = useQuery(sunmiCloudPrintersQuery, {
		variables: {
			model: 'SUNMI_CLOUD_PRINTER',
			params: { cursors: { limit: 10000 } }
		},
		fetchPolicy: 'cache-and-network'
	})
	const printerOptions = !Array.isArray(parameters?.installedPrinters) ? [] :
		parameters.installedPrinters
			.filter(f => deviceType === 'MOBILE_WAITER' ? f : f !== 'CCV A920')
			.map(item => ({
				value: item,
				label: item
			}))

	const hardwareIdList = devicesRes.data?.listDevices?.items?.map(({ hardwareId }) => hardwareId) || []
	const connectionOptions = [
		{ label: 'USB', value: 'USB' },
		{ label: 'Wi-Fi', value: 'Wi-Fi' },
		{ label: 'Bluetooth', value: 'Bluetooth' }
	]

	return(
		<ExpandableBlocks nested>
			<ContentBlock label={t('options_section.strings.receipt')} startExpanded>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					{({ formState }) => {
						const printerName = formState.values?.parameters?.printer?.name
						const printerConnectionType = formState.values?.parameters?.printer?.connectionType
						const doWeShowConnectionType = (deviceType === 'SMART_CHECKOUT') && (printerName?.indexOf('SUNMI') === -1)
						// show printerIp input when printerName hasn't 'SUNMI' in it (exception 'SUNMI Cloud Printer')
						// and when printerConnectionType not equal Bluetooth or USB
						const doWeShowPrinterIp = (printerName?.indexOf('SUNMI') === -1 || printerName === 'SUNMI Cloud Printer')
							&& printerConnectionType !== 'Bluetooth' && printerConnectionType !== 'USB'
							&& printerName !== 'CCV A920'

						return (
							<>
								<ReceiptSettingsTitle>{t('options_section.fields.printer_settings')}</ReceiptSettingsTitle>
								<Row>
									<Col xs>
										{!supportsMultipleReceiptPrinters &&
											<>
												<Select
													field='parameters.printer.name'
													label={t('options_section.fields.receipt_printer')}
													options={printerOptions}
													isClearable
												/>
												{doWeShowConnectionType &&
													<Select
														field='parameters.printer.connectionType'
														label={t('options_section.fields.connection_type')}
														options={connectionOptions}
													/>
												}
											</>
										}
										{doWeShowPrinterIp && !supportsMultipleReceiptPrinters &&
											<Text
												field='parameters.printer.ip'
												label={t('options_section.strings.serial_number')}
												validate={(v, vals) => {
													const type = vals.parameters.printer.name
													if(!type) {
														return null
													}
													if(!v) {
														return t('validators.required')
													}
													const valueIsIp = isIP(v)
													if(type.indexOf('EPSON') !== -1 && !valueIsIp) {
														return t('validators.not_a_valid_ip_address')
													}
													const valueIsValidHWID = hardwareIdList.includes(v)
													if(type.indexOf('SUNMI') !== -1 && !(valueIsIp || valueIsValidHWID)) {
														return t('validators.ipaddress_or_registered_hwid')
													}
												}}
											/>
										}
										<Divider/>
									</Col>
								</Row>
								<Row>
									<Col xs={6}>
										<ImageUploaderGraphql
											key={cashRegisterId}
											imgKey={parameters?.logo?.img?.key}
											id={cashRegisterId}
											type='PrinterLogo'
											getQuery={getCashRegister}
											field='parameters.logo.img'
											label={t('options_section.fields.logo_on_receipt')}
											elementMaxWidth={400}
											width={400}
											height={200}
											updateCacheFunction={(draft, { mutationResult, queryField }) => {
												const newImg = typeof mutationResult === 'object' ? mutationResult : null
												set(draft, `${queryField}.parameters.logo.img`, newImg)
												set(draft, `${queryField}.parameters.logo.__typename`, 'LogoClientImg')
											}}
										/>
									</Col>
									<Col xs={6}>
										<Divider/>
										<Checkbox field='parameters.hasBarcodeByReceiptTotal' label={t('options_section.fields.receipt_with_barcode')} />
										<Checkbox field='parameters.printer.printCartQR' label={t('options_section.fields.print_cart_qr')} />
										<Checkbox field='parameters.showPriceOnCreditCustomerBill' label={t('options_section.strings.showPriceOnCreditCustomerBill')} />
									</Col>
								</Row>
								{supportsMultipleReceiptPrinters &&
									<MobileWaiterReceiptPrinters
										key={cashRegisterId}
										formData={formData}
										data={data}
										printerOptions={printerOptions}
										hardwareIdList={hardwareIdList}
										designProject={data.parameters?.designProject}
									/>
								}
							</>
						)
					}}
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.labels')}>
				<Form
					smartSubmit
					initialValues={formData}
					onSubmit={handleSubmit}
					className={globalStyles['flex-cols']}
				>
					<Row>
						<Col xs>
							<Text field='parameters.printer.labelTitle' label={t('options_section.fields.label_title')} />
							<Select
								field='parameters.printer.defaultLabelID'
								label={t('options_section.fields.default_label')}
								remoteOptions={{
									query: loadAppConstants,
									variables: {
										language: i18n.language,
										type: 85
									},
									modifyOptions: options => options.map(({ label, value }) => {
										const numVal = Number(value)
										return { label, value: numVal }
									})
								}}
							/>
						</Col>
						<Col xs>
							<Checkbox field='parameters.builtInFonts' label={t('options_section.fields.use_built_in_font_for_labels')} />
							<Checkbox field='parameters.printer.automaticLabelPrint' label={t('options_section.fields.auto_label_print')} />
							<NumberInput field='parameters.printer.labelOffset' label={t('options_section.fields.label_offset')} />
						</Col>
					</Row>
				</Form>
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.kitchen')}>
				<PrinterLocation
					data={data}
					formData={formData}
					parameters={parameters}
					updateItem={updateItem}
					appData={appData}
				/>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default Printers
